import { z } from 'zod'

/**
 * The tab's copy of an unsubmitted request-for-information draft, so a reload
 * resumes where the merchant left off.
 *
 * sessionStorage, never localStorage: the draft holds compliance answers and
 * links to identity and bank documents, and on a shared or office computer
 * localStorage would keep them after the tab is gone. It is keyed by user and
 * organization so another identity in the same tab never reads it, and only
 * one draft is kept at a time.
 *
 * Deliberately not stored: identity verification (only Plaid knows whether it
 * passed; the API records the session, not the outcome, so a stored flag
 * could not be checked) and anything secret (link tokens, bank account
 * numbers). Document links are the bare object URLs `useDocumentUpload`
 * returns, with the presign query already stripped: nothing here expires, and
 * the form shows only the file name.
 */

const KEY_PREFIX = 'nickel:rfiDraft:'

const documentSchema = z.object({ name: z.string(), s3Url: z.string() })

const draftSchema = z.object({
  request: z.string(),
  currentStep: z.enum([
    'get-started',
    'additional-info',
    'identity',
    'documents',
    'bank-account',
  ]),
  acceptedTerms: z.boolean(),
  explainer: z.string(),
  documents: z.object({
    proofOfAddress: documentSchema.nullable(),
    certificate: documentSchema.nullable(),
  }),
  bankLinked: z.enum(['plaid', 'manual']).nullable(),
  manualBankDocumentS3Urls: z.array(z.string()),
  manualBankSummary: z
    .object({ bankName: z.string(), last4: z.string() })
    .nullable(),
})

export type RfiDraft = z.infer<typeof draftSchema>

export function rfiDraftKey(userId: string, organizationId: string): string {
  return `${KEY_PREFIX}${userId}:${organizationId}`
}

function draftKeysIn(storage: Storage): string[] {
  const keys: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key?.startsWith(KEY_PREFIX)) keys.push(key)
  }
  return keys
}

export function readRfiDraft(key: string): RfiDraft | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = draftSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

export function writeRfiDraft(key: string, draft: RfiDraft): void {
  removeRfiDrafts({ except: key })
  try {
    sessionStorage.setItem(key, JSON.stringify(draft))
  } catch {
    // Storage full or blocked: the draft still lives in memory until reload.
  }
}

export function hasStoredRfiDraft(key: string): boolean {
  try {
    return sessionStorage.getItem(key) !== null
  } catch {
    return false
  }
}

export function removeRfiDrafts({ except }: { except?: string } = {}): void {
  try {
    for (const key of draftKeysIn(sessionStorage)) {
      if (key !== except) sessionStorage.removeItem(key)
    }
  } catch {
    // Nothing readable, so nothing to remove.
  }
}
